/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { GraphqlServerModule } from '@vlinder-web/graphql-server-module-react';
import { GET_ORG_COMPLIANCE_DETAIL } from '../graphql';
import { VApplication } from '@/application';
import { usePolicyId } from '../provider';
import { toHeaderCase } from 'js-convert-case';
import config from '../config/featuresSection.config.json';
import moment from 'moment';
import jwt_decode from 'jwt-decode';

import { AppNavigatorService } from '@/AppNavigator/services';

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync('services.AppNavigatorService') as AppNavigatorService;
};

export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync('services.GraphqlServerModule') as GraphqlServerModule;
};

const lookup = require('country-code-lookup');

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;

  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }
  k;
  async getProps(args: any) {
    const { policyNumber } = usePolicyId();
    let vuid = jwt_decode(getWhoamiSrv()?.getToken());

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient('graphql-gateway');
    try {
      if (client) {
        const res = await client.query({
          query: GET_ORG_COMPLIANCE_DETAIL,
          variables: {
            input: {
              policyId: policyNumber,
            },
            input1: {
              limit: 100,
              where: [
                {
                  column: 'txnOwner',
                  op: 'Equal',
                  value: [vuid?.vuid],
                },
                {
                  column: 'type',
                  op: 'In',
                  value: ['POLICY_PURCHASE_FLOW', 'KYC_SUBMISSION'],
                },
                {
                  column: 'txnSetId',
                  op: 'In',
                  value: [policyNumber, null],
                },
              ],
            },
          },
          fetchPolicy: 'network-only',
        });
        if (res?.data?.getMyPolicyDetail) {
          console.log(`policy details`, res?.data?.getMyPolicyDetail);
          let _result = res?.data?.getMyPolicyDetail;
          let _txtResult = res?.data?.getTxnList;
          return {
            title: _result?.policyNumber,
            subTitle: _result?.policyNumber,
            listItems: this.getListItems(_result),
            accordionItems: this.getAccordionItems(_result, _txtResult?.data),
            policyDocument: `data:application/pdf;base64,${_result?.pdf?.pdf}`,
            carouselItems: this.getCarouselItems(_result?.pdf),
            useOfProceedDetails: this.getUseOfProceeds(_result),
            buttons: config?.buttons,
          };
          // return {
          //   policyNumber: res?.data?.purchasePolicy?.policyNumber,
          //   email: res?.data?.purchasePolicy?.purchasedBy?.email
          // }
        }
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
    // return { ...(config || {}) };
  }

  private getCarouselItems(pdf: any) {
    let _temp = [];
    if (pdf?.pdf) {
      _temp.push({
        type: 'pdf',
        poster: `data:application/pdf;base64,${pdf?.pdf}`,
        src: `data:application/pdf;base64,${pdf?.pdf}`,
        title: 'Policy Contract',
      });
    }
    return _temp;
  }

  private getAccordionItems(res: any, txList: any) {
    let items: any = [];
    let _policyHolder = this.getPolicyDetails(res);
    if (res?.description) {
      items.push({
        type: 'text',
        title: 'Description',
        data: [res?.description],
      });
    }
    if (_policyHolder) {
      items?.push({
        type: 'multi-col-list',
        numberOfColumns: '3',
        title: _policyHolder?.title,
        data: [..._policyHolder?.listItems],
      });
    }
    if (res?.benificiaryDetail) {
      let _temp = this.getBeneficiary(res?.benificiaryDetail);
      items.push({
        type: 'multi-col-list',
        numberOfColumns: '3',
        title: _temp?.title,
        data: [..._temp?.listItems],
      });
    }

    let _useOfProceeds = this.getUseOfProceeds(res);
    if (_useOfProceeds) {
      items.push({
        type: 'check-list',
        title: _useOfProceeds?.title,
        data: [..._useOfProceeds?.listItems],
      });
    }
    if (txList?.length > 0) {
      let timelineDetails = this.getTimeLine(txList);
      console.log(`txList`, timelineDetails);
      items.push({
        type: 'timeline',
        title: config?.timelineDetails?.title,
        data: [...timelineDetails],
        lineColor: '#262B40',
        horizontalStepperColors: {
          DONE: '#56DCBA',
          UNDONE: '#56DCBA',
          LAST: '#56DCBA',
          CURRENT: '#56DCBA',
        },
      });
    }
    return items;
  }

  private getTimeLine(data: any) {
    let _temp: any = [];
    Array.isArray(data) &&
      data?.forEach((element) => {
        _temp.push({
          key: element?.id,
          headline: toHeaderCase(element?.action ?? ''),
          headlineBackgroundColor: '#579783',
          children: this.getChildren(element),
        });
      });

    return _temp;
  }

  private getChildren(data: any) {
    let _temp: any = [];
    _temp.push({
      itemId: data?.id,
      title: toHeaderCase(data?.action ?? ''),
      multiColList: this.getProvenanceAccordion(data),
      subtitle: data?.when ? moment(parseInt(data?.when)).format('LLL') : null,
    });
    return _temp;
  }

  private getProvenanceAccordion(res: any) {
    let _temp: any = [];
    if (res) {
      let _list: any = [];
      let _date: any = [];
      let _location: any = [];
      if (res?.performedBy?.firstName) {
        _list.push({
          title: toHeaderCase('by'),
          subTitle: `${toHeaderCase(
            res?.performedBy?.firstName
          )} ${toHeaderCase(res?.performedBy?.lastName)}`,
          icon: config?.orgIcon,
        });
      }
      if (res?.location) {
        _location.push({
          title: toHeaderCase('at'),
          subTitle: res?.location ?? '-',
          icon: config?.locationIcon,
        });
      }
      if (res?.when) {
        _date.push({
          title: toHeaderCase('on'),
          subTitle: res?.when ? moment(parseInt(res?.when)).format('LLL') : '-',
          icon: config?.dateIcon,
        });
      }
      _temp = [..._list, ..._location, ..._date];
    }
    return {
      numberOfColumns: '2',
      data: _temp,
    };
  }

  private getBeneficiary(res: any) {
    let _beneficiary = {
      firstName: res?.firstName,
      lastName: res?.lastName,
      email: res?.email,
      phoneNumber: res?.mobileNo,
      streetAddress: res?.address?.addressLine1,
      city: res?.address?.city,
      country: res?.address?.country
        ? lookup.byIso(`${res?.address?.country}`)?.country
        : '-',
    };
    let _temp = [];
    for (const [key, value] of Object.entries(_beneficiary)) {
      _temp.push({
        title: toHeaderCase(key),
        subTitle: value,
      });
    }
    return {
      title: config?.beneficiaryDetails?.title,
      listItems: _temp,
    };
  }

  private getPolicyDetails(res: any) {
    let _temp = [
      {
        title: 'Policy Number',
        subTitle: res?.policyNumber,
      },
      {
        title: 'Policy Option',
        subTitle: `Policy Option ${res?.productDefination}`,
      },
      {
        title: 'Coverage Amount',
        subTitle: res?.coverage,
      },
      {
        title: 'Annual Premium',
        subTitle: res?.premium,
      },
      {
        title: 'Purchase on',
        subTitle: moment(res?.purchasedAt).format('DD/MM/YYYY'),
      },
      {
        title: 'Policy Type',
        subTitle: toHeaderCase(res?.type ?? '-'),
      },
      {
        title: 'Insured Country',
        subTitle: res?.countryToBeInsured
          ? lookup.byIso(`${res?.countryToBeInsured}`)?.country
          : '-',
      },
    ];
    return {
      title: config?.policyDetails?.title,
      listItems: _temp,
    };
  }
  private getListItems(res: any) {
    let _temp = config?.listItems?.map((ele: any) => {
      if (ele?.key === 'policyName') {
        return {
          ...ele,
          subTitle: `Policy Option ${res?.productDefination}`,
        };
      }
      if (ele?.key === 'coverage') {
        return {
          ...ele,
          subTitle: res?.coverage,
        };
      }
      if (ele?.key === 'premium') {
        return {
          ...ele,
          subTitle: res?.premium,
        };
      }
      if (ele?.key === 'country') {
        return {
          ...ele,
          subTitle: res?.countryToBeInsured
            ? lookup.byIso(`${res?.countryToBeInsured}`)?.country
            : '-',
          flag: res?.countryToBeInsured,
        };
      }
    });
    return _temp;
  }

  private getUseOfProceeds(data: any) {
    let _temp: any = [];
    Array.isArray(data?.useOfProceeds) &&
      data?.useOfProceeds?.forEach((element: any) => {
        if (element.selected) {
          _temp.push(toHeaderCase(element?.value ?? ''));
        }
      });

    return {
      title: config?.useOfProceedsDetail?.title,
      listItems: _temp,
    };
  }

  private getTransactionDetail(items: any) {
    let _temp = [];
    _temp =
      Array.isArray(items?.data) &&
      items?.data?.map((ele) => {
        console.log(`date when:`, new Date(ele?.when));
        return {
          title: toHeaderCase(ele?.action),
          cardTitle: toHeaderCase(ele?.action),
          cardSubtitle: ele?.payload?.policyNumber,
          cardDetailedText: ele?.when
            ? moment(parseInt(ele?.when)).format('LLL')
            : null,
        };
      });

    return {
      title: config?.timelineDetails?.title,
      listItems: _temp,
    };
  }
}
